import * as React from 'react';
import styled from 'styled-components';
import {useRoute} from 'react-router5';
import {StudentObject} from 'api/student/types';
import useLoading from 'hooks/use-loading';
import InviteButton from './components/InviteButton';
import {getStudents} from 'api/course';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import Table from 'components/Table';
import {UseTableHooks} from 'react-table';
import {UserObject} from 'stores/UserStore';
import {getImageByPreview} from 'api/library';
import moment from 'moment';
import Tabbar from 'components/Tabbar';
import Tab from 'components/Tab';
import Switch, {SwitchVariant} from '../../../Switch';
import {DropdownAlign, DropdownSide} from '../../../Dropdown';
import {StudentAfterAccess} from '../../../StudentAfterAccess';

export interface StudentsProps {

}

export type Student = Pick<UserObject,
  | 'name'
  | 'avatar'>;

export type Contacts = UserObject['contacts'] & {
  email: UserObject['email'];
};

const StudentWrapper = styled.div`
  gap: 10px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

const Avatar = styled.img`
  object-fit: cover;
  border-radius: 50%;
  height: 40px;
  width: 40px;
`;

const ContactsWrapper = styled.div`
  gap: 4px;
  display: inline-grid;
`;

const RightSide = styled.div`
  justify-content: flex-end;
  align-content: center;
  display: flex;
`;

/**
 * This component renders the settings of the course students.
 * @param {StudentsProps} props
 * @return {React.ReactElement<StudentsProps>}
 * @constructor
 */
export default function Students(
    props: StudentsProps,
): React.ReactElement<StudentsProps> {
  const {route} = useRoute();
  const {data, fetch, render} = useLoading<StudentObject[]>();
  console.log(data);

  React.useEffect(() => {
    fetch(() => new Promise((resolve) => (
      fetchStudent()
    )));
  }, []);

  /**
   * Fetch students.
   * @return {void}
   */
  function fetchStudent(): void {
    fetch(() => new Promise((resolve) => (
      getStudents(route.params?.id).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )));
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    return (
      <div>
        <InviteButton
          onInvite={() => fetchStudent()}
        />
        <br/>
        <br/>
        <br/>
        <Switch
          label="Разрешить студентам видеть друг друга"
          variant={SwitchVariant.Accent}
        />
        <br/>
        <br/>
        <Tabbar param="type">
          <Tab name="pending" title="Запросы">
            <Table
              columns={[
                {
                  Header: '',
                  accessor: 'toggle',
                  Cell: (props: UseTableHooks<Pick<StudentObject, 'paymentStatus'>>) => (
                    <Switch />
                  ),
                },
                {
                  Header: '#',
                  accessor: 'intId',
                },
                {
                  Header: 'Имя студента',
                  accessor: 'student',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Student>) => (
                    <StudentWrapper>
                      <Avatar
                        src={getImageByPreview(props.cell.value.avatar)}
                      />
                      <a href={`/students/${props.cell.value.id}`} target="_blank" rel="noreferrer">
                        {props.cell.value.name}
                      </a>
                    </StudentWrapper>
                  ),
                },
                {
                  Header: 'Контакты',
                  accessor: 'contacts',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Contacts>) => (
                    <ContactsWrapper>
                      <div>{props.cell.value.email}</div>
                      <div>{props.cell.value.phone}</div>
                      <div>{props.cell.value.telegram}</div>
                      <div>{props.cell.value.instagram}</div>
                      <div>{props.cell.value.youtube}</div>
                    </ContactsWrapper>
                  ),
                },
                {
                  Header: 'Дата и время',
                  accessor: 'paymentDate',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Pick<StudentObject, 'paymentDate'>>) => (
                    <ContactsWrapper>
                      <div>{moment(props.cell.value).format('HH:MM | DD.MM.YYYY')}</div>
                    </ContactsWrapper>
                  ),
                },
                {
                  Header: '',
                  accessor: 'paymentStatus',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Pick<StudentObject, 'paymentStatus'>>) => (
                    <RightSide>
                      <StudentAfterAccess
                        side={DropdownSide.Bottom}
                        align={DropdownAlign.Right}
                      />
                    </RightSide>
                  ),
                },
              ]}
              data={data?.filter((student) => (
                student.paymentStatus === 'pending'
              )).map((student) => ({
                toggle: student.name,
                intId: student.intId,
                student: {
                  avatar: student.avatar,
                  name: student.name,
                  id: student._id,
                },
                contacts: {
                  email: student.email,
                  ...student.contacts,
                },
                paymentDate: student.paymentDate,
                paymentStatus: student.paymentStatus,
              })) || []}
            />
          </Tab>
          <Tab name="success" title="Текущие">
            <Table
              columns={[
                {
                  Header: 'Имя студента',
                  accessor: 'student',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Student>) => (
                    <StudentWrapper>
                      <Avatar
                        src={getImageByPreview(props.cell.value.avatar)}
                      />
                      {props.cell.value.name}
                    </StudentWrapper>
                  ),
                },
                {
                  Header: 'Контакты',
                  accessor: 'contacts',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Contacts>) => (
                    <ContactsWrapper>
                      <div>{props.cell.value.email}</div>
                      <div>{props.cell.value.phone}</div>
                      <div>{props.cell.value.telegram}</div>
                      <div>{props.cell.value.instagram}</div>
                      <div>{props.cell.value.youtube}</div>
                    </ContactsWrapper>
                  ),
                },
                {
                  Header: 'Дата и время',
                  accessor: 'paymentDate',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Pick<StudentObject, 'paymentDate'>>) => (
                    <ContactsWrapper>
                      <div>{moment(props.cell.value).format('HH:MM | DD.MM.YYYY')}</div>
                    </ContactsWrapper>
                  ),
                },
                {
                  Header: '',
                  accessor: 'paymentStatus',
                  // eslint-disable-next-line react/display-name
                  Cell: (props: UseTableHooks<Pick<StudentObject, 'paymentStatus'>>) => (
                    <ContactsWrapper>
                      <div>{props.cell.value}</div>
                    </ContactsWrapper>
                  ),
                },
              ]}
              data={data?.filter((student) => (
                student.paymentStatus === 'success'
              )).map((student) => ({
                student: {
                  avatar: student.avatar,
                  name: student.name,
                },
                contacts: {
                  email: student.email,
                  ...student.contacts,
                },
                paymentDate: student.paymentDate,
                paymentStatus: student.paymentStatus,
              })) || []}
            />
          </Tab>
        </Tabbar>
      </div>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <div>
        Not found
      </div>
    );
  }

  return render(renderContent, renderNotFound);
}
